import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useETLService } from './hooks/useETLService';
import { useCustomers } from './hooks/useCustomers';
import ETLStatusPanel from './components/ETLStatusPanel';
import ETLJobHistory from './components/ETLJobHistory';
import ETLScheduleConfig from './components/ETLScheduleConfig';
import DataSourcesList from './components/DataSourcesList';
import DBExplorer from './components/DBExplorer';
import WarehouseBrowser from './components/WarehouseBrowser';
import OCELQueryConfig from './components/OCELQueryConfig';
import ExtractionSourcesConfig from './components/ExtractionSourcesConfig';
import StepsPipelinePanel from './components/StepsPipelinePanel';
import StepDebugPanel from './components/StepDebugPanel';
import LoggingPanel from './components/LoggingPanel';
import CustomerSelector from './components/CustomerSelector';
import ETLDebugPanel from './components/ETLDebugPanel';
import { useStepPipeline } from './hooks/useStepPipeline';

// ── Tab definitions ──────────────────────────────────────────────────────────

const TABS = [
  {
    id: 'projects',
    label: 'Projects',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v8.25" />
      </svg>
    ),
  },
  {
    id: 'warehouse',
    label: 'Warehouse',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 5.625c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
      </svg>
    ),
  },
  {
    id: 'sql',
    label: 'SQL Query',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="m6.75 7.5 3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0 0 21 18V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v12a2.25 2.25 0 0 0 2.25 2.25Z" />
      </svg>
    ),
  },
  {
    id: 'data-sources',
    label: 'Data Sources',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 5.625c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
      </svg>
    ),
  },
  {
    id: 'ocel-config',
    label: 'OCEL Config',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
      </svg>
    ),
  },
  {
    id: 'pipeline',
    label: 'Pipeline',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
  },
  {
    id: 'logging',
    label: 'Logging',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="m6.75 7.5 3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0 0 21 18V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v12a2.25 2.25 0 0 0 2.25 2.25Z" />
      </svg>
    ),
  },
];

// Tabs reachable without the page's own selectedCustomerId being set — Logging
// has its own independent, read-only customer picker (see LoggingPanel).
const CUSTOMER_GATE_EXEMPT_TABS = new Set(['projects', 'logging']);

// ── PipelinePanel ────────────────────────────────────────────────────────────

function PipelinePanel({ customerId, health, schedule, jobs, loading, etlServiceReachable, triggeringJob, onTrigger, configOpen, onToggleConfig, onAssembleDone, onScheduleSaved, isActiveTab }) {
  const [triggerError, setTriggerError] = useState(null);
  const [fullPipelineOpen, setFullPipelineOpen] = useState(false);

  const { steps, loading: stepsLoading, error: stepsError, runStep } = useStepPipeline(
    etlServiceReachable,
    { customerId, onAssembleDone, isActiveTab },
  );

  const handleFullTrigger = async () => {
    setTriggerError(null);
    try {
      await onTrigger();
    } catch (err) {
      setTriggerError(err?.response?.data?.detail ?? err.message ?? 'Trigger failed');
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {/* Left column */}
      <div className="space-y-4">
        <ETLStatusPanel
          health={health}
          schedule={schedule}
          jobs={jobs}
          etlServiceReachable={etlServiceReachable}
          loading={loading}
        />

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Step-by-Step Pipeline</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Trigger each OCEL generation step individually. Intermediate results are saved to disk between steps.
            </p>
          </div>
          <StepsPipelinePanel
            etlServiceReachable={etlServiceReachable}
            steps={steps}
            loading={stepsLoading}
            error={stepsError}
            runStep={runStep}
            customerId={customerId}
          />

          <div className="pt-3 border-t border-gray-100">
            <button
              onClick={() => setFullPipelineOpen(v => !v)}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
            >
              <svg
                className={`w-3 h-3 transition-transform ${fullPipelineOpen ? 'rotate-90' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
              Run full pipeline in one shot
            </button>
            {fullPipelineOpen && (
              <div className="mt-2 flex items-center justify-between">
                <div className="text-xs text-gray-400">
                  Transformer: <code className="bg-gray-100 px-1 rounded text-gray-600">{schedule?.transformer_id ?? 'ethen_warehouse'}</code>
                </div>
                <button
                  onClick={handleFullTrigger}
                  disabled={!etlServiceReachable || triggeringJob}
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  {triggeringJob ? (
                    <>
                      <span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" />
                      Running…
                    </>
                  ) : (
                    <>
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                      </svg>
                      Trigger Now
                    </>
                  )}
                </button>
              </div>
            )}
            {triggerError && (
              <p className="mt-1 text-xs text-red-600 bg-red-50 rounded px-2 py-1">{triggerError}</p>
            )}
          </div>
        </div>

        <ETLScheduleConfig
          customerId={customerId}
          schedule={schedule}
          open={configOpen}
          onToggle={onToggleConfig}
          onSaved={onScheduleSaved}
        />

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">OCEL Transformation</h3>
          </div>
          <div className="p-4 space-y-3">
            <ConfigRow label="Transformer" value={schedule?.transformer_id ?? 'ethen_full'} envVar="TRANSFORMER_ID" desc="Which transformer converts warehouse data to OCEL 2.0." />
            <ConfigRow label="Intermediate Path" value="/app/intermediate" envVar="INTERMEDIATE_PATH" desc="Where object/event parquets are stored between steps." />
            <ConfigRow label="OCEL Output Path" value="/app/ocel_output" envVar="OCEL_OUTPUT_PATH" desc="Shared volume path where generated OCEL JSON files are written." />
            <ConfigRow label="Warehouse Path" value="/app/warehouse/warehouse.duckdb" envVar="WAREHOUSE_PATH" desc="Path to the DuckDB file on the shared dlt-warehouse volume." />
          </div>
        </div>
      </div>

      {/* Right column */}
      <div className="space-y-4">
        <ETLJobHistory jobs={jobs} loading={loading} />
        <StepDebugPanel steps={steps} customerId={customerId} />
      </div>
    </div>
  );
}

function ConfigRow({ label, value, envVar, desc }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-xs font-medium text-gray-600">{label}</span>
        <code className="text-[10px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">{envVar}</code>
      </div>
      <div className="font-mono text-xs bg-gray-50 border border-gray-100 rounded px-2.5 py-1.5 text-gray-700 mb-0.5">
        {value}
      </div>
      <p className="text-[11px] text-gray-400">{desc}</p>
    </div>
  );
}

// ── No-customer placeholder ──────────────────────────────────────────────────

function NoCustomerPrompt() {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 1 1.242 7.244" />
        </svg>
      </div>
      <p className="text-white/70 text-sm font-medium">Connect a customer to continue</p>
      <p className="text-white/40 text-xs mt-1">Select a customer above and click Connect to establish the VPN tunnel.</p>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function ETLProjectPage({ onFileChange }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('projects');
  const [configOpen, setConfigOpen] = useState(false);
  // Bumped whenever Data Sources saves, so OCEL Config's alias dropdown refetches
  // without requiring a page reload — see ExtractionSourcesConfig/OCELQueryConfig.
  const [extractionSourcesVersion, setExtractionSourcesVersion] = useState(0);

  const {
    customers,
    selectedCustomerId,
    connectingId,
    disconnectingId,
    loading: customersLoading,
    connect,
    disconnect,
    selectCustomer,
  } = useCustomers({
    onCustomerActive: useCallback((customerId) => {
      // When a customer becomes active for the first time, auto-select it
    }, []),
  });

  const {
    health,
    jobs,
    schedule,
    etlFiles,
    builtInFiles,
    activeFileType,
    loading,
    etlServiceReachable,
    triggeringJob,
    activatingFile,
    refreshingFiles,
    triggerJob,
    activateFile,
    refreshFiles,
    refreshSchedule,
  } = useETLService({
    customerId: selectedCustomerId,
    onFileActivated: (fileType) => {
      onFileChange?.(fileType);
      navigate('/dashboard');
    },
  });

  const PAGE_TITLES = {
    projects: { title: 'Projects', subtitle: 'Select a dataset to begin analysis, or trigger the ETL pipeline to create one.' },
    warehouse: { title: 'Data Warehouse', subtitle: 'Browse tables in the DuckDB warehouse populated by the ETL pipeline.' },
    sql: { title: 'DB Explorer', subtitle: 'Step-by-step: verify VPN → database connection → browse tables → run SQL queries.' },
    'data-sources': { title: 'Data Sources', subtitle: 'Define named SQL queries that extract data from SQL Server.' },
    'ocel-config': { title: 'OCEL Config', subtitle: 'Map each OCEL input to one of your extracted sources, then configure column mappings.' },
    pipeline: { title: 'ETL Pipeline', subtitle: 'Monitor, trigger, and configure the data extraction pipeline.' },
    logging: { title: 'Logging', subtitle: 'Review job and step-run history for any connected customer.' },
  };

  const { title, subtitle } = PAGE_TITLES[activeTab];
  const hasActiveCustomer = !!selectedCustomerId;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(135deg, #2C65E8 0%, #41CCAB 100%)' }}
    >
      {/* Header bar */}
      <div className="flex items-center justify-between h-[64px] px-6 flex-shrink-0">
        <img
          src="/shape_Logo_final_Designversionen_000_transparent.png"
          alt="Shape Process Intelligence"
          className="h-[38px]"
        />
        {activeFileType && (
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Enter Dashboard
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 min-h-0 px-6 pb-0 gap-5">
        {/* Sidebar navigation */}
        <aside className="w-44 flex-shrink-0 pt-4 flex flex-col gap-0.5">
          <p className="text-white/40 text-[10px] font-semibold uppercase tracking-widest px-3 mb-3">
            Navigation
          </p>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              disabled={!hasActiveCustomer && !CUSTOMER_GATE_EXEMPT_TABS.has(tab.id)}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left ${
                !hasActiveCustomer && !CUSTOMER_GATE_EXEMPT_TABS.has(tab.id)
                  ? 'text-white/30 cursor-not-allowed'
                  : activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-white/75 hover:bg-white/10 hover:text-white'
              }`}
            >
              <span className={activeTab === tab.id ? 'text-primary-600' : !hasActiveCustomer && !CUSTOMER_GATE_EXEMPT_TABS.has(tab.id) ? 'text-white/20' : 'text-white/60'}>
                {tab.icon}
              </span>
              {tab.label}
            </button>
          ))}
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 pt-4 overflow-y-auto">
          {/* Customer selector — always visible */}
          <CustomerSelector
            customers={customers}
            selectedCustomerId={selectedCustomerId}
            connectingId={connectingId}
            disconnectingId={disconnectingId}
            loading={customersLoading}
            onConnect={connect}
            onDisconnect={disconnect}
            onSelect={selectCustomer}
          />

          {/* Page heading */}
          <div className="mb-5">
            <h1 className="text-2xl font-bold text-white">{title}</h1>
            <p className="text-white/65 text-sm mt-1">{subtitle}</p>
          </div>

          {/* Logging tab is always mounted — it has its own independent, read-only
              customer picker and must work with zero customers connected on this page. */}
          <div style={{ display: activeTab === 'logging' ? undefined : 'none' }}>
            <LoggingPanel />
          </div>

          {/* Gate non-project tabs on having an active customer. Logging is exempt
              (its own panel above handles the no-customer case) and, unlike 'projects',
              doesn't need the six customer-scoped panels mounted underneath it. */}
          {!hasActiveCustomer && activeTab !== 'projects' && activeTab !== 'logging' ? (
            <NoCustomerPrompt />
          ) : !hasActiveCustomer && activeTab === 'logging' ? null : (
            <>
              <div style={{ display: activeTab === 'projects' ? undefined : 'none' }}>
                <DataSourcesList
                  etlFiles={etlFiles}
                  builtInFiles={builtInFiles}
                  activeFileType={activeFileType}
                  activatingFile={activatingFile}
                  refreshingFiles={refreshingFiles}
                  etlServiceReachable={etlServiceReachable}
                  onActivate={activateFile}
                  onRefresh={refreshFiles}
                />
              </div>

              <div style={{ display: activeTab === 'warehouse' ? undefined : 'none' }}>
                <WarehouseBrowser
                  key={selectedCustomerId}
                  customerId={selectedCustomerId}
                  etlServiceReachable={etlServiceReachable}
                />
              </div>

              <div style={{ display: activeTab === 'sql' ? undefined : 'none' }}>
                <DBExplorer
                  key={selectedCustomerId}
                  customerId={selectedCustomerId}
                  etlServiceReachable={etlServiceReachable}
                />
              </div>

              <div style={{ display: activeTab === 'data-sources' ? undefined : 'none' }}>
                <ExtractionSourcesConfig
                  customerId={selectedCustomerId}
                  onSourcesSaved={() => setExtractionSourcesVersion(v => v + 1)}
                />
              </div>

              <div style={{ display: activeTab === 'ocel-config' ? undefined : 'none' }}>
                <OCELQueryConfig
                  customerId={selectedCustomerId}
                  etlServiceReachable={etlServiceReachable}
                  extractionSourcesVersion={extractionSourcesVersion}
                />
              </div>

              <div style={{ display: activeTab === 'pipeline' ? undefined : 'none' }}>
                <PipelinePanel
                  key={selectedCustomerId}
                  customerId={selectedCustomerId}
                  health={health}
                  schedule={schedule}
                  jobs={jobs}
                  loading={loading}
                  etlServiceReachable={etlServiceReachable}
                  triggeringJob={triggeringJob}
                  onTrigger={triggerJob}
                  configOpen={configOpen}
                  onToggleConfig={() => setConfigOpen(v => !v)}
                  onAssembleDone={refreshFiles}
                  onScheduleSaved={refreshSchedule}
                  isActiveTab={activeTab === 'pipeline'}
                />
              </div>
            </>
          )}
        </main>
      </div>

      {/* Connection debug bar — always visible, outside tabs */}
      <ETLDebugPanel customers={customers} connectingId={connectingId} />
    </div>
  );
}
